// biome-ignore lint/correctness/noNodejsModules: This page uses hashing in getServerSideProps.
import { createHash } from 'node:crypto';

import { withBxApiContext } from 'helpers/withBxApiContext';
import nookies from 'nookies';
import bxApi from 'services/bxApi';

import {
  BuyerIntentCategoryView,
  BuyerIntentSubject,
  getBuyerIntentCategoryId,
  getBuyerIntentListingProductIds,
  warnMissingBuyerIntentIds,
} from 'components/BuyerIntent';
import { transformToBackendSerpCategoryKeyFeatures } from 'data/ai-content-rich';
import { buildListingCtaUrls } from 'helpers/buildCtaUrls';
import { buildBxApiFilter, hasInvalidFilterParams } from 'helpers/bxApiFilter';
import { mergeSerpFacets } from 'helpers/dataTransformers/serp';
import { getGeoipDetails } from 'helpers/geoipDetector';
import immediatelyResolve from 'helpers/immediatelyResolve';
import { getAppData, setCacheTags } from 'helpers/initializePage';
import hasALiveChatCategory, { hasPplCategory } from 'helpers/liveChat';
import { ROUTE_NAMES } from 'routes';
import Serp from 'screens/serp';
import {
  iseElegibleUrlForMetasExperiment,
  setMetasForSeoExperiment,
} from 'screens/serp/helpers/seoExperiments/metas';
import { contentfulMainSiteService } from 'services/contentful';
import getAppService from 'services/getapp';
import { getPplCategories } from 'services/pplCategories/pplCategories';
import routeService from 'services/routes';

Serp.ddogName = 'serp';

const PRODUCT_LIST_BATCH_SIZE = 25;
const DEFAULT_OG_LOGO = '/i/og_logo.jpg';
const DEFAULT_TWITTER_SITE_CONTENT = '@GetApp';
const DEFAULT_BING_SITE_VERIFICATION = 'F982D3BE475FCD2842ACB655B1E89582';
const PREMIUM_STATUS_IDENTIFIER = 'U';
const BASIC_STATUS_IDENTIFIER = 'B';
const QUERY_FILTERED_ROBOTS = 'noindex, nofollow';

// Path-segment params that are never included in query strings
const PATH_PARAMS = new Set([
  'header',
  'categorySlug',
  'filter',
  'facet',
  'pagination',
  'page',
  'geoip',
]);

// Build a query string from ctx.query, excluding path routing params.
// excludeSort=true when building sort destination URLs (sort is being replaced).
// excludeSort=false when building pagination URLs (current sort should be preserved).
const buildQueryString = (query, excludeSort = false) =>
  Object.entries(query)
    .filter(
      ([key]) => !(PATH_PARAMS.has(key) || (excludeSort && key === 'sort'))
    )
    .map(([key, value]) =>
      Array.isArray(value)
        ? value
            .map((v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`)
            .join('&')
        : `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
    )
    .join('&');

// Returns the category base path, preserving any path-based facet segment (e.g. /os/mac/, /w/slack/).
const buildCategoryBasePath = (headerSlug, categorySlug, query) =>
  query.facet && query.filter
    ? `/${headerSlug}-software/${categorySlug}/${query.facet}/${query.filter}/`
    : `/${headerSlug}-software/${categorySlug}/`;

const normalizePathname = (path) => {
  if (!path) {
    return null;
  }

  let pathname;

  try {
    pathname = path.startsWith('http')
      ? new URL(path).pathname
      : path.split('?')[0].split('#')[0];
  } catch {
    return null;
  }

  const absolutePathname = pathname.startsWith('/') ? pathname : `/${pathname}`;
  return absolutePathname.endsWith('/')
    ? absolutePathname
    : `${absolutePathname}/`;
};

const buildRedirectSearch = (searchParams) => {
  const redirectSearchParams = new URLSearchParams(searchParams);

  PATH_PARAMS.forEach((param) => {
    redirectSearchParams.delete(param);
  });

  const search = redirectSearchParams.toString();
  return search ? `?${search}` : '';
};

const buildCanonicalSerpRedirect = ({
  categoryUrl,
  currentUrl,
  host,
  query,
}) => {
  const requestedBasePath = normalizePathname(
    `/${query.header}-software/${query.categorySlug}/`
  );
  const canonicalBasePath = normalizePathname(categoryUrl);

  if (!canonicalBasePath || canonicalBasePath === requestedBasePath) {
    return null;
  }

  const currentUrlParts = new URL(currentUrl, host);
  const currentPathname = normalizePathname(currentUrlParts.pathname);
  const suffix = currentPathname?.startsWith(requestedBasePath)
    ? currentPathname.slice(requestedBasePath.length)
    : '';
  const search = buildRedirectSearch(currentUrlParts.searchParams);

  return `${canonicalBasePath}${suffix}${search}`;
};

const buildSortingOptions = (headerSlug, categorySlug, query = {}) => {
  const filterParams = buildQueryString(query, true);
  const basePath = buildCategoryBasePath(headerSlug, categorySlug, query);

  const buildUrl = (sortSlug) => {
    const params = filterParams
      ? `${filterParams}&sort=${sortSlug}`
      : `sort=${sortSlug}`;
    return `${basePath}?${params}`;
  };

  return [
    {
      label: 'Sponsored',
      slug: 'relevance',
      url: buildUrl('relevance'),
      selected: false,
    },
    {
      label: 'User rating',
      slug: 'reviews',
      url: buildUrl('reviews'),
      selected: false,
    },
  ];
};

const getBuyersGuide = async (ctx) =>
  contentfulMainSiteService({
    preview: ctx.query.preview,
    environment: ctx.req?.headers['fe-contentful-env'],
  })
    .getBuyersGuide(ctx.query.categorySlug)
    .catch(() => null);

const SORT_SLUG_TO_BX_API = {
  relevance: ['AUCTION_POSITION_DIRECTORY', 'HIGHEST_RATED_BAYESIAN_SCORE'],
  reviews: ['HIGHEST_RATED_BAYESIAN_SCORE'],
};

export const SERP_CATEGORY_ROUTE_NAME = 'serp_category';

// Maps facet path segments to their corresponding route names
// Matches backend Rails routes: /f/ -> serp_tag, /os/ -> serp_oses, etc.
const FACET_TO_ROUTE_NAME = {
  f: 'serp_tag', // Feature tags
  os: 'serp_oses', // Operating systems
  p: 'serp_pricingcategories', // Pricing categories
  w: 'serp_integrated_with', // Integrated with
  org: 'serp_typecustomers', // Customer types
};

/**
 * Determines the route_name based on the selected facet filter.
 * @param {string|undefined} facet - The facet type (e.g., 'f', 'os', 'p', 'w', 'org')
 * @returns {string} The route name (e.g., 'serp_tag', 'serp_category')
 */
const getSerpRouteName = (facet) =>
  FACET_TO_ROUTE_NAME[facet] || SERP_CATEGORY_ROUTE_NAME;

/**
 * Maps route names to GA4 content groups.
 * Matches backend logic in app/controllers/concerns/api.rb#ga4_content_group
 * @param {string} routeName - The route name (e.g., 'serp_category', 'serp_tag')
 * @returns {string} The GA4 content group
 */
const getGA4ContentGroup = (routeName) => {
  const contentGroupMap = {
    serp_category: 'Category/Software',
    serp_oses: 'Category/Software Deployment',
    serp_pricingcategories: 'Category/Software Pricing',
    serp_typecustomers: 'Category/Software Organization',
    serp_tag: 'Category/Software Features',
    serp_integrated_with: 'Category/Software Integrations',
  };
  return contentGroupMap[routeName] || 'Category/Software';
};

export const buildFallbackSerpTabUrls = (
  categoryDetailUrl,
  routeName = SERP_CATEGORY_ROUTE_NAME
) => [
  {
    label: 'All apps',
    slug: 'all-products',
    route_name: routeName,
    url: categoryDetailUrl,
  },
  {
    label: 'Guide',
    slug: 'guide',
    route_name: routeName,
    url: `${categoryDetailUrl}#guide`,
  },
];

const formatFacetBreadcrumbLabel = (filterSlug) =>
  filterSlug
    ? String(filterSlug)
        .replace(/-/g, ' ')
        .replace(/^\w/, (char) => char.toUpperCase())
    : null;

const isOlderThanSixMonths = (value) => {
  if (!value) {
    return false;
  }

  const updatedAt = new Date(value);
  if (Number.isNaN(updatedAt.getTime())) {
    return false;
  }

  const threshold = new Date();
  threshold.setMonth(threshold.getMonth() - 6);
  return updatedAt < threshold;
};

export const buildFallbackSerpBreadcrumb = (label, url, childLabel = null) =>
  label ? [{ label, url }, ...(childLabel ? [{ label: childLabel }] : [])] : [];

export const buildFallbackSerpPageHeader = ({
  categoryName,
  directoryH1,
  featureLabel,
  page,
}) => {
  const baseTitle = featureLabel
    ? `Top Rated ${categoryName?.includes('Software') ? categoryName : `${categoryName} Software`} with ${featureLabel}`
    : directoryH1 ||
      (categoryName?.includes('Software')
        ? categoryName
        : `${categoryName} Software`);
  return page > 1 ? `${baseTitle} - Page ${page}` : baseTitle;
};

const buildFallbackSerpTitle = ({
  categoryName,
  directoryH1,
  featureLabel,
  facetType,
  page,
}) => {
  const currentYear = new Date().getFullYear();
  const categoryWithSoftware = categoryName?.includes('Software')
    ? categoryName
    : `${categoryName} Software`;

  let baseTitle;

  if (facetType) {
    baseTitle = `Top Rated ${categoryWithSoftware} with ${featureLabel} ${currentYear}`;
  } else {
    // No filters - serp.category.title pattern
    // "%{name} Software %{year} - Best Application Comparison"
    baseTitle = `${directoryH1 || categoryWithSoftware} ${currentYear} - Best Application Comparison`;
  }

  // Pagination: "Page %{page} | " prefix for paginated pages
  return page > 1 ? `Page ${page} | ${baseTitle}` : baseTitle;
};

export const buildFallbackSerpMetaDescription = ({
  categoryName,
  featureLabel,
  metaDescription,
}) => {
  if (featureLabel) {
    const currentYear = new Date().getFullYear();
    const categoryWithSoftware = categoryName?.includes('Software')
      ? categoryName
      : `${categoryName} software`;
    return `Discover the top-rated ${categoryWithSoftware} with ${featureLabel} in ${currentYear}. Compare verified user ratings & reviews to find the best match for your business size, need & industry.`;
  }

  if (metaDescription) {
    return metaDescription;
  }

  return `Discover the top ${categoryName} software for small businesses to enterprises on GetApp in ${new Date().getFullYear()}. Compare and filter by 1.5M verified user reviews, features, integrations, pricing, and more.`;
};

const buildPaginationUrl = ({
  basePath,
  hasActiveFilters,
  page,
  paginationQueryString,
}) => {
  if (page <= 1) {
    return paginationQueryString
      ? `${basePath}?${paginationQueryString}`
      : basePath;
  }

  if (hasActiveFilters) {
    return `${basePath}?${paginationQueryString ? `${paginationQueryString}&` : ''}page=${page}`;
  }

  return `${basePath}page-${page}/`;
};

const buildSerpLinkRel = ({
  appHost,
  basePath,
  hasActiveFilters,
  isQueryFiltered,
  numPages,
  page,
  paginationQueryString,
}) => {
  if (isQueryFiltered) {
    return [];
  }

  const canonicalPath = buildPaginationUrl({
    basePath,
    hasActiveFilters,
    page,
    paginationQueryString,
  });

  const links = [{ url: `${appHost}${canonicalPath}`, rel: 'canonical' }];

  if (page > 1) {
    links.push({
      url: `${appHost}${buildPaginationUrl({
        basePath,
        hasActiveFilters,
        page: page - 1,
        paginationQueryString,
      })}`,
      rel: 'prev',
    });
  }

  if (page < numPages) {
    links.push({
      url: `${appHost}${buildPaginationUrl({
        basePath,
        hasActiveFilters,
        page: page + 1,
        paginationQueryString,
      })}`,
      rel: 'next',
    });
  }

  return links;
};

const buildHashId = (ctx) =>
  createHash('md5')
    .update(
      ctx.req.headers['cf-connecting-ip'] ||
        ctx.req.headers['x-forwarded-for'] ||
        ctx.req.socket?.remoteAddress ||
        ''
    )
    .digest('hex');

const getListingStatusIdentifier = (listing) =>
  listing?.is_premium ? PREMIUM_STATUS_IDENTIFIER : BASIC_STATUS_IDENTIFIER;

const getSerpListingTrackingValues = (listings) =>
  listings.flatMap((listing) => {
    const localId = listing?.id;
    return localId ? [`${localId}${getListingStatusIdentifier(listing)}`] : [];
  });

const buildSerpDataLayer = ({
  appData,
  category,
  geoipDetails,
  ctx,
  listings,
  route,
  contentGroupGA4,
  contentType,
  contentRichSection,
}) => {
  const { countryCode, provider } = geoipDetails;
  const localIds = getSerpListingTrackingValues(listings);

  const dataLayer = {
    routeName: route,
    hashID: buildHashId(ctx),
    geoipCountryCode: countryCode,
    geoipProvider: provider,
    userId: appData.header?.user?.id || null,
    ldContextUuid: null,
    browserName: appData.userAgent?.browser || null,
    contentGroupGA4,
    headerID: null,
    categoryID: category?.id ?? null,
    categoryPageUuid: category?.global_category_id || null,
    dimension59_with_localids: localIds.join(','),
    dimension59_with_uuids: listings
      .map((listing) =>
        listing?.global_product_id
          ? `${listing.global_product_id}${getListingStatusIdentifier(listing)}`
          : null
      )
      .filter(Boolean)
      .join(','),
  };

  // Add Content Rich AI fields if applicable
  if (contentType && contentRichSection) {
    dataLayer.content_type = contentType;
    dataLayer.contentRichSection = contentRichSection;
  }

  return [dataLayer];
};

const buildCategoryHasSeoFacets = (publishedFacets) =>
  Object.values(publishedFacets?.seoFacetValues || {}).some(
    (values) => Array.isArray(values) && values.length > 0
  );

const toArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];

const hasUnknownPublishedFacetValue = (query, publishedFacets) => {
  if (!(publishedFacets && Object.keys(publishedFacets).length > 0)) {
    return false;
  }

  const availableValues = {
    f: new Set(
      publishedFacets?.seoFacetValues?.feature_tags_facet ||
        (publishedFacets?.feature_tags_facet || []).map((item) => item.value)
    ),
    w: new Set(
      publishedFacets?.seoFacetValues?.integrated_with_facet ||
        (publishedFacets?.integrated_with_facet || []).map((item) => item.value)
    ),
    os: new Set(
      publishedFacets?.seoFacetValues?.oses_facet ||
        publishedFacets?.oses_facet ||
        []
    ),
    p: new Set(
      publishedFacets?.seoFacetValues?.pricingcategories_facet ||
        publishedFacets?.pricingcategories_facet ||
        []
    ),
    org: new Set(
      publishedFacets?.seoFacetValues?.typecustomers_facet ||
        publishedFacets?.typecustomers_facet ||
        []
    ),
  };

  const queryValues = {
    f: toArray(query['tag_slug[]'] || query.tag_slug),
    w: toArray(query['integrated_with_slug[]'] || query.integrated_with_slug),
    os: toArray(query['oses_slug[]'] || query.oses_slug),
    p: toArray(
      query['pricingcategories_slug[]'] || query.pricingcategories_slug
    ),
    org: toArray(query['typecustomers_slug[]'] || query.typecustomers_slug),
  };

  if (query.facet && query.filter) {
    const allowedValues = availableValues[query.facet];
    if (allowedValues && !allowedValues.has(String(query.filter))) {
      return true;
    }
  }

  return Object.entries(queryValues).some(([facetKey, values]) => {
    if (values.length === 0) {
      return false;
    }

    const allowedValues = availableValues[facetKey];
    return values.some((value) => !allowedValues?.has(String(value)));
  });
};

const getServerSidePropsWithBxApi = async (ctx, url) => {
  if (hasInvalidFilterParams(ctx.query)) {
    ctx.res.statusCode = 404;
    return {
      props: { appData: getAppData(ctx, {}), error: { statusCode: 404 } },
    };
  }

  const publishedFacetsPromise = bxApi
    .findPublishedCategoryFacets(ctx.query.categorySlug)
    .catch(() => ({}));

  // For feature facets, pre-fetch the BX published feature list to resolve
  // feature UUIDs for BX API filtering. Covers both the URL-path facet
  // (/f/slug/) and sidebar multi-selection (?tag_slug[]=a&tag_slug[]=b).
  const hasFeatureFilter =
    (ctx.query.facet === 'f' && ctx.query.filter) ||
    ctx.query['tag_slug[]'] ||
    ctx.query.tag_slug;
  const prefetchedFeatureFacets = hasFeatureFilter
    ? await publishedFacetsPromise
    : null;
  const selectedFeatureFacet =
    ctx.query.facet === 'f' && ctx.query.filter
      ? prefetchedFeatureFacets?.feature_tags_facet?.find(
          (facet) => facet.value === ctx.query.filter
        ) || null
      : null;

  const filter = buildBxApiFilter(ctx, prefetchedFeatureFacets);
  const requestedSortSlug = ctx.query.sort;
  const sort =
    SORT_SLUG_TO_BX_API[requestedSortSlug] || SORT_SLUG_TO_BX_API.relevance;
  const geoipDetails = getGeoipDetails(ctx);
  const { countryCode } = geoipDetails;

  const paginationQueryString = buildQueryString(ctx.query);
  const isQueryFiltered = paginationQueryString !== '';
  const isClientNavigation = Boolean(
    ctx.req.headers['x-nextjs-data'] ||
      ctx.req.url.includes(ROUTE_NAMES.NEXT_DATA)
  );
  const variables = {
    countryCode,
    slug: ctx.query.categorySlug,
    batchSize: PRODUCT_LIST_BATCH_SIZE,
    afterCursor: null,
    filter,
    includeSimilarAlternatives: Boolean(
      ctx.query.facet &&
        ctx.query.filter &&
        !isQueryFiltered &&
        !isClientNavigation
    ),
    selectedFeatureIds: selectedFeatureFacet?.global_product_id
      ? [selectedFeatureFacet.global_product_id]
      : undefined,
    sort,
  };

  const page =
    Number.parseInt(ctx.query.pagination, 10) ||
    Number.parseInt(ctx.query.page, 10) ||
    1;

  if (page > 1) {
    const itemsToSkip = PRODUCT_LIST_BATCH_SIZE * (page - 1);
    const bxPageInfo = await bxApi.fetchDirectoryCategoryProductsPageInfo({
      ...variables,
      batchSize: itemsToSkip,
    });
    variables.afterCursor = bxPageInfo?.endCursor;
  }

  const isMainDirectory = !url.match(/\/(f|org|os|p|w)\/|\/page-\d+($|\/)/);
  const { getUser } = getAppService(ctx.req, url);

  const requests = [
    bxApi
      .fetchDirectoryCategoryProductsCount({
        ...variables,
        batchSize: 100,
        afterCursor: null,
      })
      .catch(() => 0),
    bxApi
      .fetchDirectoryCategoryProducts({ ...variables })
      .catch((error) => ({ error })),
    getUser().catch(() => null),
    isMainDirectory
      ? bxApi
          .getDirectoryHreflangsBySlug(ctx.query.categorySlug)
          .catch(() => [])
      : immediatelyResolve([]),
    isMainDirectory ? getBuyersGuide(ctx) : immediatelyResolve(),
    isMainDirectory
      ? bxApi
          .getPopularComparisonsByCategorySlug(
            countryCode,
            ctx.query.categorySlug
          )
          .catch(() => [])
      : immediatelyResolve([]),
    immediatelyResolve({ top_featured_products: [], top_rated_products: [] }),
    getPplCategories({ countryCode }).catch(() => []),
    publishedFacetsPromise,
  ];

  const results = await Promise.all(requests);

  const [
    totalProductsCount,
    directoryCategoryResponse,
    user,
    hreflangs,
    buyersGuide,
    comparisons,
    {
      top_featured_products: topFeaturedProducts,
      top_rated_products: topRatedProducts,
    },
    pplCategories,
    publishedFacets,
  ] = results;

  if (hasUnknownPublishedFacetValue(ctx.query, publishedFacets)) {
    const appData = { ...getAppData(ctx, {}), hreflangs, url };
    ctx.res.statusCode = 404;
    return { props: { appData, error: { statusCode: 404 } } };
  }

  const directoryCategoryError = directoryCategoryResponse?.error;
  if (directoryCategoryError?.message?.includes('Category not found')) {
    const appData = { ...getAppData(ctx, {}), hreflangs, url };
    ctx.res.statusCode = 404;
    return { props: { appData, error: { statusCode: 404 } } };
  }

  const directoryCategoryData = directoryCategoryResponse?.error
    ? {}
    : directoryCategoryResponse;
  const categoryHasSeoFacets = buildCategoryHasSeoFacets(publishedFacets);
  const facets = mergeSerpFacets({
    rawFacets: {},
    publishedFacets,
    query: ctx.query,
    header: ctx.query.header,
    categorySlug: ctx.query.categorySlug,
    categoryHasSeoFacets,
  });

  let appData = getAppData(ctx, {});
  appData = { ...appData, hreflangs, url };

  // BX-API failure: .catch(() => ({})) returns an object without `products`.
  // A real empty category returns { products: [] }. Show error instead of 0 results.
  if (directoryCategoryData?.products === undefined) {
    ctx.res.statusCode = 503;
    return { props: { appData, error: { statusCode: 503 } } };
  }

  const listings = directoryCategoryData.products;

  const mergedListings = listings.map((listing) => {
    const builtCtaUrls = buildListingCtaUrls({
      slug: listing.slug,
      headerSlug: ctx.query.header,
    });

    return {
      is_category_leader: false,
      is_saved: false,
      most_visited: false,
      category_leaders_score: null,
      ...listing,
      // is_premium is already set correctly by transformProduct in bxApi/client.js
      cta_urls: {
        ...builtCtaUrls,
        ...listing.cta_urls,
        // For non-premium products, use detail URL as fallback for cta
        cta: listing.cta_urls?.cta || builtCtaUrls.detail,
      },
    };
  });

  const getTotalPages = (itemCount) =>
    Math.ceil((itemCount || PRODUCT_LIST_BATCH_SIZE) / PRODUCT_LIST_BATCH_SIZE);

  const paginationSuffix = paginationQueryString
    ? `?${paginationQueryString}`
    : '';
  const basePath = buildCategoryBasePath(
    ctx.query.header,
    variables.slug,
    ctx.query
  );
  // Use path-based pagination (/page-2/) only on the main category page (no query filters, no path facet).
  // Use query-based pagination (?page=2) when any filter is active to preserve filter state.
  const hasActiveFilters =
    isQueryFiltered || !!(ctx.query.facet && ctx.query.filter);
  const other_page_pattern = hasActiveFilters
    ? `${basePath}?${paginationQueryString ? `${paginationQueryString}&page=PAGE` : 'page=PAGE'}`
    : `${basePath}page-PAGE/`;

  const pagination = {
    num_items: totalProductsCount,
    num_pages: getTotalPages(totalProductsCount),
    page: page,
    items_per_page: PRODUCT_LIST_BATCH_SIZE,
    first_page: `${basePath}${paginationSuffix}`,
    other_page_pattern,
  };

  const bxCategory = directoryCategoryData?.category || {};
  if (bxCategory.isPagePublished === false) {
    ctx.res.statusCode = 404;
    return { props: { appData, error: { statusCode: 404 } } };
  }

  const canonicalRedirect = buildCanonicalSerpRedirect({
    categoryUrl: bxCategory.url,
    currentUrl: url,
    host: appData.host,
    query: ctx.query,
  });

  if (canonicalRedirect) {
    return {
      redirect: {
        destination: canonicalRedirect,
        statusCode: 301,
      },
    };
  }

  // Build breadcrumb label for any facet type (f, os, p, w, org)
  const facetBreadcrumbLabel =
    ctx.query.facet && ctx.query.filter
      ? formatFacetBreadcrumbLabel(ctx.query.filter)
      : null;
  const shouldShowLastUpdatedOn = selectedFeatureFacet
    ? bxCategory.last_updated_on || null
    : isOlderThanSixMonths(bxCategory.last_updated_on)
      ? null
      : bxCategory.last_updated_on || null;
  const categoryDetailUrl = `/${ctx.query.header}-software/${ctx.query.categorySlug}/`;
  const category = {
    ...bxCategory,
    global_category_id: bxCategory.id || null,
    cta_urls: { detail: categoryDetailUrl },
    definition: bxCategory.shortDescription || null,
    has_seo_facets: categoryHasSeoFacets,
    last_updated_on: shouldShowLastUpdatedOn,
  };

  const pageHeader = buildFallbackSerpPageHeader({
    categoryName: category.name,
    directoryH1: bxCategory.directoryH1,
    featureLabel: facetBreadcrumbLabel,
    page,
  });
  const title = buildFallbackSerpTitle({
    categoryName: category.name,
    directoryH1: bxCategory.directoryH1,
    featureLabel: facetBreadcrumbLabel,
    facetType: ctx.query.facet || null,
    page,
  });
  const description = buildFallbackSerpMetaDescription({
    categoryName: category.name,
    featureLabel: facetBreadcrumbLabel,
    metaDescription: bxCategory.metaDescription,
  });

  const routeName = getSerpRouteName(ctx.query.facet);

  appData.category = category;
  // Build header in UI for all facet types (serp_category, serp_tag, serp_oses, etc.)
  appData.header = {
    ...appData.header,
    user,
    tab_urls: buildFallbackSerpTabUrls(categoryDetailUrl, routeName),
    breadcrumb: buildFallbackSerpBreadcrumb(
      category.name,
      categoryDetailUrl,
      facetBreadcrumbLabel
    ),
  };
  appData.head = {
    ...appData.head,
    title,
    page_header: pageHeader,
    category: {
      slug: ctx.query.categorySlug,
    },
    meta: {
      description,
      robots: isQueryFiltered ? QUERY_FILTERED_ROBOTS : null,
      category_software_number: totalProductsCount,
      og: {
        og_logo: `${appData.host}${DEFAULT_OG_LOGO}`,
        og_title: facetBreadcrumbLabel
          ? title
          : page === 1
            ? `Find the right ${category.name} software`
            : pageHeader,
        og_desc: description,
        twitter_site_content: DEFAULT_TWITTER_SITE_CONTENT,
        extra_metas: [
          { name: 'msvalidate.01', content: DEFAULT_BING_SITE_VERIFICATION },
          { name: 'description', content: description },
          { name: 'category_software_number', content: totalProductsCount },
        ],
      },
    },
    link_rel: buildSerpLinkRel({
      appHost: appData.host,
      basePath,
      hasActiveFilters,
      isQueryFiltered,
      numPages: pagination.num_pages,
      page,
      paginationQueryString,
    }),
  };

  // Load SERP category key features from JSON files when feature facet is selected
  // Must be loaded before building data_layer to determine content_type and contentRichSection
  let key_features = [];
  if (selectedFeatureFacet && category.name) {
    try {
      // Convert to lowercase to match file naming pattern
      // Files are named as: "{category}_{feature}.json" (all lowercase, spaces preserved)
      const categoryFileName = category.name.toLowerCase();
      const featureFileName = selectedFeatureFacet.label.toLowerCase();
      const fileName = `${categoryFileName}_${featureFileName}.json`;

      const serpCategoryKeyFeaturesData = await import(
        `data/ai-content-rich/serp-category-key-features/${fileName}`
      ).then((module) => module.default);

      if (
        serpCategoryKeyFeaturesData &&
        Array.isArray(serpCategoryKeyFeaturesData) &&
        serpCategoryKeyFeaturesData.length > 0
      ) {
        const transformedKeyFeatures =
          transformToBackendSerpCategoryKeyFeatures(
            serpCategoryKeyFeaturesData
          );
        if (transformedKeyFeatures) {
          key_features = transformedKeyFeatures;
        }
      }
    } catch (_error) {}
  }

  // Build content_type and contentRichSection for Content Rich AI pages
  // Only applies to feature facet pages (serp_tag) with AI-generated content
  let contentType = null;
  let contentRichSection = null;

  if (routeName === 'serp_tag' && key_features && key_features.length > 0) {
    contentType = 'Content Rich AI';
    contentRichSection = 'key-features';

    // Check if products AI summaries exist (best-for lists)
    const hasProductsSummaries =
      topRatedProducts.length > 0 || topFeaturedProducts.length > 0;

    if (hasProductsSummaries) {
      // Check if products have media (editorial content)
      const hasMediaContent =
        topRatedProducts.some((p) => p.media) ||
        topFeaturedProducts.some((p) => p.media);

      contentRichSection += hasMediaContent
        ? ', best-for-list-features-editorial'
        : ', best-for-list-features';
    }
  }

  const contentGroupGA4 = getGA4ContentGroup(routeName);

  appData.head.data_layer = buildSerpDataLayer({
    appData,
    category,
    geoipDetails,
    ctx,
    listings: mergedListings,
    route: routeName,
    contentGroupGA4,
    contentType,
    contentRichSection,
  });
  appData.head.google_analytics = {
    content_group: routeName,
  };
  appData.route = routeName;

  if (
    iseElegibleUrlForMetasExperiment(url || ctx.req.url) &&
    appData.head?.meta
  ) {
    setMetasForSeoExperiment({
      head: appData.head,
      category,
      pagination,
    });
  }

  ctx.req.APP_DATA = appData;
  setCacheTags(ctx, ROUTE_NAMES.SERP);

  const [{ geoipCountryCode } = {}] = appData.head?.data_layer || [];

  const hasPplCategoryValue = hasPplCategory(
    pplCategories,
    ctx.query.categorySlug
  );
  appData.head.hasPplCategory = hasPplCategoryValue;
  appData.head.hasLiveChatEnabled = hasALiveChatCategory(pplCategories, [
    appData.head.category,
  ]);
  appData.head.hasClickToCall =
    ['US', 'CA'].includes(geoipCountryCode) && appData.head.hasLiveChatEnabled;
  appData.head.hasPplExitIntentEnabled =
    ['US', 'CA'].includes(geoipCountryCode) && hasPplCategoryValue;
  appData.head.hasPplExpertFormEnabled =
    ['US', 'CA'].includes(geoipCountryCode) && hasPplCategoryValue;
  appData.head.hasPplDemoFormEnabled =
    ['US', 'CA'].includes(geoipCountryCode) && hasPplCategoryValue;

  if (bxCategory.id) {
    const prevLastSeenCategories = JSON.parse(
      ctx.req.cookies?.last_seen_categories || '[]'
    );
    const lastSeenCategories = [
      ...prevLastSeenCategories,
      { id: bxCategory.id, slug: bxCategory.slug },
    ]
      .filter(
        (cat, index, arr) => arr.findIndex((c) => c.id === cat.id) === index
      )
      .slice(-10);
    nookies.set(
      ctx,
      'last_seen_categories',
      JSON.stringify(lastSeenCategories),
      {
        path: '/',
        maxAge: 30 * 24 * 60 * 60,
      }
    );
  }

  return {
    props: {
      appData,
      serpData: {
        route: routeName,
        route_name: routeName,
        content_type: contentType,
        contentRichSection,
        category,
        feature: selectedFeatureFacet
          ? { label: selectedFeatureFacet.label }
          : null,
        key_features,
        listings: mergedListings,
        comparisons: comparisons || [],
        facets,
        pagination,
        sorting_options: buildSortingOptions(
          ctx.query.header,
          ctx.query.categorySlug,
          ctx.query
        ).map((opt) => ({
          ...opt,
          selected: opt.slug === (ctx.query.sort || 'relevance'),
        })),
        related_categories: (
          directoryCategoryData?.related_categories || []
        ).map((rc) => ({
          ...rc,
          cta_urls: {
            ...rc.cta_urls,
            detail:
              rc.cta_urls?.detail ||
              `/${ctx.query.header}-software/${rc.slug}/`,
          },
        })),
        // Temporary UI-only kill switch for the SERP content rich layout.
        // Previous gate: topRatedProducts.length >= 3 && !!feature
        crxEnabled: false,
        topFeaturedProducts,
        topRatedProducts,
      },
      buyersGuide,
    },
  };
};

export const getServerSideProps = withBxApiContext('serp', async (ctx) => {
  if (
    (ctx.query.pagination && Number.isNaN(Number(ctx.query.pagination))) ||
    (ctx.query.page && Number.isNaN(Number(ctx.query.page)))
  ) {
    return {
      redirect: {
        destination: routeService.generatePath(ROUTE_NAMES.SERP, {
          ...ctx.query,
          pagination: undefined,
        }),
        statusCode: 301,
      },
    };
  }

  let { url } = ctx.req;
  if (ctx.req.url.includes(ROUTE_NAMES.NEXT_DATA)) {
    try {
      url = routeService.generatePath(ROUTE_NAMES.SERP, ctx.query);
    } catch (_error) {
      return { notFound: true };
    }
    const params = routeService.getParams(ctx.query, [
      'header',
      'categorySlug',
      'filter',
      'facet',
      'pagination',
    ]);
    if (params) {
      url += `?${params}`;
    }
  }

  return getServerSidePropsWithBxApi(ctx, url);
});

const SerpPage = (props) => {
  const categoryId = getBuyerIntentCategoryId(props.appData);
  const productIds = getBuyerIntentListingProductIds(props.serpData?.listings);

  if (!categoryId) {
    warnMissingBuyerIntentIds('page:serp', {
      categoryId:
        props.serpData?.category?.global_category_id ||
        props.appData?.head?.data_layer?.[0]?.categoryPageUuid,
    });

    return <Serp {...props} />;
  }

  return (
    <BuyerIntentCategoryView categoryId={categoryId} sourceLocation="page:serp">
      <BuyerIntentSubject productIds={productIds} />
      <Serp {...props} />
    </BuyerIntentCategoryView>
  );
};

SerpPage.ddogName = Serp.ddogName;
SerpPage.hasBuyerIntentPageView = true;

export default SerpPage;
